/****************************************************
 * Module Name:  CriteriaInvoiceDetail.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Box, Button, CircularProgress, Tooltip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import ReportProxy from "../../utility/proxy/manager/reportProxy";
import Modal from "react-bootstrap/Modal";
import Constants from "../../utility/constants";
import "./../../style/modalstyle.css";
import CommonSnackbar from "../common/commonSnackBar";
import logo from '../../images/pansuriya.jpg'
import StoneDetailModal from "../common/stoneDetailModal";

const CriteriaInvoiceDetail = ({ criteriaData, handleClose, show }) => {

    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [detailLoader, setDetailLoader] = useState(false);
    const [data, setData] = useState([]);
    const [filterValues, setFilterValues] = useState({});
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [stoneId, setStoneId] = useState("");

    useEffect(() => {
        handleSearch(criteriaData);
    }, []);

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };


    const handleSearch = (criteriaData) => {
        setDetailLoader(true);
        ReportProxy.GetInvoiceDetailByCriteria(
            criteriaData,
            (response) => {
                if (response) {
                    const uniqueFilterValues = {
                        customercompanyname: Utility.getDistinctOptions(response, "customercompanyname"),
                        exchangecurrency: Utility.getDistinctOptions(response, "exchangecurrency"),
                        user: Utility.getDistinctOptions(response, "userid"),
                        broker: Utility.getDistinctOptions(response, "brokerid"),
                        customer: Utility.getDistinctOptions(response, "customerid"),
                        invoicecustomer: Utility.getDistinctOptions(response, "invoicecustomerid"),
                        tradingcompany: Utility.getDistinctOptions(response, "tradingcompanyid"),
                    };
                    const combinedFilterValues = {
                        customercompanyname: uniqueFilterValues.customercompanyname,
                        status: uniqueFilterValues.status,
                        exchangecurrency: uniqueFilterValues.exchangecurrency,
                        user: uniqueFilterValues.user.map(value => {
                            const matchingItem = response.find(item => item.userid === value);
                            return matchingItem ? `${value} | ${matchingItem.username}` : [];
                        }),
                        broker: uniqueFilterValues.broker.map(value => {
                            const matchingItem = response.find(item => item.brokerid === value);
                            if (matchingItem) {
                                let displayValue = matchingItem.brokerid === -1 ? " - " : `${matchingItem.brokerid} | ${matchingItem.brokercompanyname}`;
                                return displayValue;
                            }
                            return [];
                        }),
                        customer: uniqueFilterValues.customer.map(value => {
                            const matchingItem = response.find(item => item.customerid === value);
                            return matchingItem ? `${value} | ${matchingItem.customercompanyname}` : [];
                        }),
                        invoicecustomer: uniqueFilterValues.invoicecustomer.map(value => {
                            const matchingItem = response.find(item => item.invoicecustomerid === value);
                            if (matchingItem) {
                                let displayValue = (!matchingItem.invoicecustomerid) ? " - " : `${matchingItem.invoicecustomerid} | ${matchingItem.invoicecustomercompanyname}`;
                                return displayValue;
                            }
                            return [];
                        }),
                        tradingcompany: uniqueFilterValues.tradingcompany.map(value => {
                            const matchingItem = response.find(item => item.tradingcompanyid === value);
                            return matchingItem ? `${value} | ${matchingItem.tradingcompanyname}` : [];
                        }),
                    };
                    setFilterValues(combinedFilterValues);
                    setDetailLoader(false);

                    function sortByCustomerIdOccurrence(data) {
                        // Create a map to count the occurrences of each customerid 
                        const customerCountMap = data.reduce((acc, item) => { acc[item.customerid] = (acc[item.customerid] || 0) + 1; return acc; }, {});

                        return data.sort((a, b) => {
                            const countA = customerCountMap[a.customerid];
                            const countB = customerCountMap[b.customerid];
                            if (countA !== countB)
                                return countB - countA;
                            return a.customername.localeCompare(b.customername);
                        });
                    }
                    setData(sortByCustomerIdOccurrence(response));
                }
                else {
                    setErrorSnackbar(true);
                    setDetailLoader(false);
                }
            }
        )
    };

    const initialColumns = () => [
        {
            headerName: "INVOICE ID",
            field: "invoiceid",
            headerAlign: "center",
            width: 100,
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            headerName: "STONEID",
            field: "stoneid",
            headerAlign: "center",
            width: 100,
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowStoneDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            headerName: "USER",
            field: "userid",
            headerAlign: "center",
            width: 150,
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.user : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                return params.row.isdnk ? `${params.row.username}` : `${params.row.userid} | ${params.row.username}`;
            },
            renderCell: (params) => {
                let displayValue = params.row.isdnk ? `${params.row.username}` : `${params.row.userid} | ${params.row.username}`;
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                            {displayValue}
                        </span>
                    </div>
                );
            }
        },
        {
            headerName: "BROKER COMPANY",
            field: "brokerid",
            headerAlign: "center",
            width: 200,
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.broker : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                return (params.row.brokerid === null || params.row.brokerid === -1) ? " - " : `${params.row.brokerid} | ${params.row.brokercompanyname}`;
            },
            renderCell: (params) => {
                let displayValue = (params.row.brokerid === null || params.row.brokerid === -1) ? " - " : `${params.row.brokerid} | ${params.row.brokercompanyname}`
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                            {displayValue}
                        </span>
                    </div>
                );
            },
        },
        {
            headerName: "CUSTOMER COMPANY",
            field: "customerid",
            headerAlign: "center",
            width: 300,
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.customer : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                return `${params.row.customerid} | ${params.row.customercompanyname}`;
            },
            renderCell: (params) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                            {`${params.row.customerid} | ${params.row.customercompanyname}`}
                        </span>
                    </div>
                );
            },
        },
        {
            headerName: "CUSTOMER INVOICE COMPANY",
            field: "invoicecustomerid",
            width: 300,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.invoicecustomer : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                return (!params.row.invoicecustomerid) ? " - " : `${params.row.invoicecustomerid} | ${params.row.invoicecustomercompanyname}`;
            },
            renderCell: (params) => {
                let displayValue = (!params.row.invoicecustomerid) ? " - " : `${params.row.invoicecustomerid} | ${params.row.invoicecustomercompanyname}`
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                            {displayValue}
                        </span>
                    </div>
                );
            },
            headerAlign: "center",
            align: "center",
        },
        {
            headerName: "TRADING COMPANY",
            field: "tradingcompanyid",
            headerAlign: "center",
            width: 300,
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.tradingcompany : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                return (params.row.tradingcompanyid === null || params.row.tradingcompanyid === -1) ? " - " : `${params.row.tradingcompanyid} | ${params.row.tradingcompanyname}`;
            },
            renderCell: (params) => {
                let displayValue = (params.row.tradingcompanyid === null || params.row.tradingcompanyid === -1) ? " - " : `${params.row.tradingcompanyid} | ${params.row.tradingcompanyname}`;
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                            {displayValue}
                        </span>
                    </div>
                );
            },
        },
        {
            headerName: "TRADING COMPANY LOCATION",
            field: "companylocation",
            headerAlign: "center",
            type: "singleSelect",
            width: 200,
            valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            align: "center",
        },
        {
            headerName: "SIZE",
            field: "size",
            align: "center",
            headerAlign: "center",
            width: 90,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            headerName: "TOTAL",
            field: "total",
            align: "center",
            headerAlign: "center",
            width: 90,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            headerName: "PPC",
            field: "ppc",
            align: "center",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 90,
            renderCell: renderCellExpand,
        },
        {
            headerName: "RAPNET PRICE",
            field: "rapnetprice",
            align: "center",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 180,
            renderCell: renderCellExpand,
        },

        {
            headerName: "COUNTRY OF FINAL DESTINATION",
            field: "countryoffinaldestination",
            headerAlign: "center",
            width: 190,
            align: "center",
            renderCell: renderCellExpand,
        },
        {
            headerName: "COUNTRY OF ORIGIN",
            field: "countryoforigin",
            align: "center",
            headerAlign: "center",
            width: 150,
            renderCell: renderCellExpand,
        },
        {
            headerName: "DATE",
            field: "date",
            headerAlign: "center",
            width: 80,
            align: "center",
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalDate(params.row.date),
        },
        {
            headerName: "ENSURED BY",
            field: "ensuredby",
            headerAlign: "center",
            width: 150,
            align: "center",
            renderCell: renderCellExpand,
        },
        {
            headerName: "EXCHANGE CURRENCY",
            field: "exchangecurrency",
            headerAlign: "center",
            width: 130,
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.exchangecurrency : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            headerName: "EXCHANGE RATE",
            field: "exchangerate",
            headerAlign: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            renderCell: renderCellExpand,
        },
        {
            headerName: "FINAL DESTINATION",
            field: "finaldestination",
            headerAlign: "center",
            width: 150,
            align: "center",
            renderCell: renderCellExpand,
        },
        {
            headerName: "PORT OF DISCHARGE",
            field: "portofdischarge",
            headerAlign: "center",
            width: 150,
            align: "center",
            renderCell: renderCellExpand,
        },
        {
            headerName: "PORT OF LOADING",
            field: "portofloading",
            headerAlign: "center",
            width: 130,
            align: "center",
            renderCell: renderCellExpand,
        },
        {
            headerName: "REMARKS",
            field: "remarks",
            headerAlign: "center",
            width: 80,
            align: "center",
            renderCell: renderCellExpand,
        },
        {
            headerName: "SHIPPED BY",
            field: "shippedby",
            headerAlign: "center",
            width: 150,
            align: "center",
            renderCell: renderCellExpand,
        },
        { headerName: "TERMS", field: "terms", align: "center", renderCell: renderCellExpand, },
        { headerName: "DAYS", field: "days", align: "center", renderCell: (params) => params.value, },
    ];

    const getRowClassName = (params) => {
        return params.row.isdnk ? 'purple-row' : '';
    };

    const columns = initialColumns();
    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />

            {showStoneDetailModal ? (
                <StoneDetailModal
                    show={showStoneDetailModal}
                    stoneId={stoneId}
                    handleClose={() => setShowStoneDetailModal(false)}
                />
            ) : null}

            <Modal show={show} onHide={handleClose} dialogClassName="bhb" enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Invoice Detail
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Box px={1} mt={4}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .lavenderCell": {
                                    color: "#080808",
                                    backgroundColor: "#D8BFD8",
                                },
                                height: "65vh",
                            }}
                        >

                            <DataGridPro
                                disableRowSelectionOnClick={false}
                                density="compact"
                                columns={columns}
                                rows={data}
                                getRowId={(row) => row.stoneid}
                                getRowClassName={getRowClassName}
                                loading={detailLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    '& .MuiDataGrid-row': {
                                        whiteSpace: 'nowrap',
                                    },
                                    '& .purple-row': {
                                        backgroundColor: "#B6B9D6 !important",
                                    },
                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                slots={{ toolbar: GridToolbar }}
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                                initialState={{
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                            />
                            <Box
                                style={{
                                    position: "absolute",
                                    top: 5,
                                    right: "270px",
                                    display: "flex",
                                    flexWrap: "nowrap",
                                }}
                            >
                                {detailLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() => handleSearch(criteriaData)}
                                            color="primary"
                                            fontSize="medium"
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="error" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}
export default CriteriaInvoiceDetail;